import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../../services/api';
import { defaultStartLocal } from '../../utils/optimizeScheduleUtils';
import { listCapDevices } from '../../utils/optimizeScopeUtils';
import { iperfTestFormFromRequest, iperfTestFormProblem, iperfTestFormToRequest } from '../../utils/iperfScheduleUtils';
import ScheduleWhenFields, { scheduleWhenProblems, useSiteTimezones, useTimezoneOptions } from './ScheduleWhenFields';
import IperfTestTargetFields from './IperfTestTargetFields';


/**
 * Create or edit an iperf schedule: starts the same iperf test as the Iperf
 * tab's Start Test, with these CAPs and settings, at a set site time. Each
 * run's test runs appear in the Iperf tab and are picked up by Optimize.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.schedule existing schedule to edit, or null to create
 * @param {Array} [props.devices] site devices, for the CAP picker
 * @param {() => void} props.onClose
 * @param {(schedule:object) => void} props.onSaved
 */
export default function IperfScheduleDialog({ open, schedule, devices, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const editing = Boolean(schedule);
  const { timezones, loaded: tzLoaded, error: tzError } = useSiteTimezones(open);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const caps = useMemo(() => listCapDevices(devices || []), [devices]);

  useEffect(() => {
    if (!open) {
      setForm(null);
      return;
    }
    if (form) return;
    if (editing) {
      setForm({
        name: schedule.name || '',
        enabled: schedule.enabled !== false,
        timezone: schedule.timezone || '',
        startLocal: schedule.start_local || '',
        repeat: schedule.repeat || 'daily',
        test: {
          ...iperfTestFormFromRequest(schedule.iperf_request || {}, caps),
          // The test name defaults to the schedule name; only keep a different one.
          testName: schedule.iperf_request?.name && schedule.iperf_request.name !== schedule.name ? schedule.iperf_request.name : '',
        },
      });
    } else if (tzLoaded) {
      const tz = timezones[0] || '';
      setForm({
        name: 'Nightly iperf',
        enabled: true,
        timezone: tz,
        startLocal: defaultStartLocal(tz, 1),
        repeat: 'daily',
        test: iperfTestFormFromRequest(null, caps),
      });
    }
  }, [open, form, editing, schedule, timezones, tzLoaded, caps]);

  const tzOptions = useTimezoneOptions(timezones, form?.timezone);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const problems = [];
  if (form) {
    problems.push(...scheduleWhenProblems(form));
    const testProblem = iperfTestFormProblem(form.test);
    if (testProblem) problems.push(testProblem);
  }

  const save = async () => {
    if (!form || problems.length > 0) return;
    setSaving(true);
    try {
      const iperfRequest = iperfTestFormToRequest(form.test);
      const body = {
        name: form.name.trim(),
        enabled: form.enabled,
        timezone: form.timezone,
        start_local: form.startLocal,
        repeat: form.repeat,
        iperf_request: iperfRequest,
      };
      const saved = editing
        ? await api.updateIperfSchedule(schedule.id, body)
        : await api.createIperfSchedule(body);
      enqueueSnackbar(editing ? 'Schedule updated.' : 'Schedule created.', { variant: 'success' });
      onSaved(saved);
    } catch (err) {
      enqueueSnackbar(err?.data?.error || err?.message || 'Failed to save the schedule', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>{editing ? 'Edit scheduled iperf test' : 'New scheduled iperf test'}</DialogTitle>
      <DialogContent dividers>
        {!form ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : (
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Each run starts the same iperf test as Start Test in the Iperf tab, with the CAPs and settings below. Its
              runs appear in the Iperf tab, and Optimize picks up their hop measurements. A run is skipped (and shown as
              skipped) while another iperf test, an optimize or a plan is still in progress for this site; schedule an
              optimize after the test has had time to finish.
            </Typography>
            {tzError && <Alert severity="error">{tzError}</Alert>}

            <ScheduleWhenFields form={form} onChange={set} tzOptions={tzOptions} editing={editing} />

            <Divider />

            <IperfTestTargetFields
              value={form.test}
              onChange={(patch) => setForm((f) => ({ ...f, test: { ...f.test, ...patch } }))}
              devices={devices}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {form && problems.length > 0 && (
          <Typography variant="caption" color="error" sx={{ mr: 'auto', pl: 2 }}>{problems[0]}</Typography>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!form || saving || problems.length > 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {editing ? 'Save' : 'Create schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
